import {
  CrownOutlined,
  ExperimentOutlined,
  HistoryOutlined,
  LoginOutlined,
  LogoutOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-components';
import ProLayout from '@ant-design/pro-layout';
import { Alert, Button, Dropdown, Tag, message, theme } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import logoImage from '../assets/logo.png';
import { useAuth } from '../contexts/AuthContext';
import memberService from '../services/member';
import AuthModal from './auth/AuthModal';
import WeChatCallback from './auth/WeChatCallback';
import BloodChemistryExtractor from './BloodChemistryExtractor';
import BuyMembership from './BuyMembership';
import MemberInfo from './MemberInfo';
import ProcessStatus from './ProcessStatus';
import TaskHistory from './TaskHistory';

const ProLayoutApp = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();
  
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [memberInfoVisible, setMemberInfoVisible] = useState(false);
  const [memberInfo, setMemberInfo] = useState(null);
  
  // Task management state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [taskId, setTaskId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processCompleted, setProcessCompleted] = useState(false);
  const [apiError, setApiError] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('blood-chemistry');

  // 根据路由同步 tab 状态
  useEffect(() => {
    if (location.pathname === '/task-history') {
      setActiveTab('task-history');
    } else {
      setActiveTab('blood-chemistry');
    }
  }, [location.pathname]);

  // 获取会员信息
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchMemberInfo();
    }
  }, [isAuthenticated, user]);

  const fetchMemberInfo = async () => {
    try {
      const info = await memberService.getMemberInfo();
      setMemberInfo(info);
    } catch (error) {
      console.error('获取会员信息失败:', error);
    }
  };

  // 菜单配置
  const menuDataRender = () => [
    {
      path: '/lab-extraction',
      name: '化验单提取',
      icon: <ExperimentOutlined />,
      children: [
        {
          path: '/blood-chemistry',
          name: 'A5标准化验单提取',
          icon: <ExperimentOutlined />,
        },
      ],
    },
    {
      path: '/buy-membership',
      name: '购买会员',
      icon: <ShoppingCartOutlined />,
      // Highlight membership with a badge or color if needed
    },
  ];

  // 处理登录
  const handleLogin = () => {
    setAuthModalVisible(true);
  };

  // 处理退出登录
  const handleLogout = async () => {
    try {
      await logout();
      message.success('退出登录成功');
      navigate('/blood-chemistry');
    } catch (error) {
      message.error('退出登录失败');
    }
  };

  // 用户菜单
  const userMenuItems = [
    {
      key: 'member-info',
      icon: <CrownOutlined />,
      label: '会员信息',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const handleUserMenuClick = ({ key }) => {
    switch (key) {
      case 'member-info':
        setMemberInfoVisible(true);
        break;
      case 'logout':
        handleLogout();
        break;
    }
  };

  // 渲染用户头像区域
  const renderAvatarProps = () => {
    if (!isAuthenticated) {
      return {
        render: () => (
          <Button 
            type="primary" 
            icon={<LoginOutlined />} 
            onClick={handleLogin}
            className="glass-card"
            style={{ 
              border: 'none',
              background: token.colorPrimary,
              boxShadow: '0 4px 14px rgba(79, 70, 229, 0.4)'
            }}
          >
            登录
          </Button>
        ),
      };
    }

    // 获取用户显示名称
    // 优先级: 姓名 > 微信昵称 > 邮箱前缀 > 默认
    let displayName = '用户';
    
    if (user) {
      // 检查并清理 full_name
      const fullName = user.full_name?.trim();
      if (fullName) {
        displayName = fullName;
      } else {
        // 检查并清理 wechat_nickname
        const wechatNickname = user.wechat_nickname?.trim();
        if (wechatNickname) {
          displayName = wechatNickname;
        } else if (user.email) {
          // 使用邮箱的用户名部分
          const emailPrefix = user.email.split('@')[0]?.trim();
          if (emailPrefix) {
            displayName = emailPrefix;
          }
        }
      }
    }
    
    return {
      render: () => (
        <Dropdown
          menu={{
            items: userMenuItems,
            onClick: handleUserMenuClick,
          }}
          placement="bottomRight"
        >
          <div 
            className="glass-panel"
            style={{ 
              cursor: 'pointer', 
              padding: '8px 16px',
              borderRadius: '12px',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid rgba(79, 70, 229, 0.15)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              backdropFilter: 'blur(12px)',
              minWidth: '180px',
              maxWidth: '280px',
              userSelect: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(79, 70, 229, 0.15)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div 
              className="user-display-name"
              style={{
                color: '#8b5cf6', 
                fontSize: '15px', 
                fontWeight: '600',
                flex: 1,
                minWidth: '60px',
                textAlign: 'left',
                lineHeight: '1.4',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {displayName}
            </div>
            {memberInfo && (
              <Tag 
                bordered={false}
                style={{ 
                  margin: 0,
                  fontSize: '11px',
                  padding: '3px 10px',
                  borderRadius: '10px',
                  background: memberInfo.is_member 
                    ? 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' 
                    : 'linear-gradient(135deg, #94A3B8 0%, #64748B 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                  flexShrink: 0
                }}
              >
                {memberInfo.is_member ? 'VIP' : 'FREE'}
              </Tag>
            )}
          </div>
        </Dropdown>
      ),
    };
  };

  // 处理文件处理相关的回调函数
  const handleFilesSelected = (files) => {
    setSelectedFiles(files);
    setTaskId(null);
    setProcessCompleted(false);
  };

  const handleClearFiles = () => {
    setSelectedFiles([]);
    setTaskId(null);
    setProcessCompleted(false);
  };

  const handleProcessFiles = useCallback(async (files, patientFields, projectFieldName, resultFieldName, formData, existingTaskId) => {
    if (existingTaskId) {
      setTaskId(existingTaskId);
      setIsProcessing(true);
      setApiError(null);
      setProcessCompleted(false);
      message.success('任务已启动，正在处理文件...');
      return;
    }

    if (!files || files.length === 0) {
      message.error('请先选择文件');
      return;
    }

    if (!user || !user.id) {
      message.error('用户信息无效，请重新登录');
      return;
    }

    setIsProcessing(true);
    setApiError(null);
    
    try {
      const { createTask } = await import('../services/api');
      const taskData = {
        task_name: formData?.task_name || '血化学检验报告提取',
        doc_type: 'lab_routine',
        patient_fields: patientFields,
        project_field_name: projectFieldName,
        result_field_name: resultFieldName
      };
      
      const data = await createTask(taskData);
      setTaskId(data.task_id);
      
      message.success('任务创建成功，开始处理文件');
      
    } catch (error) {
      console.error('处理文件时出错:', error);
      setApiError(error.message);
      message.error(`处理失败: ${error.message}`);
      setIsProcessing(false);
    }
  }, [user]);

  const handleTaskStatusChange = useCallback((taskData) => {
    if (taskData.status === 'completed') {
      setIsProcessing(false);
      setProcessCompleted(true);
    } else if (taskData.status === 'failed') {
      setIsProcessing(false);
      setProcessCompleted(false);
      setApiError(taskData.error_message || '任务处理失败');
    }
  }, []);

  const handleDownload = useCallback((result) => {
    console.log('下载完成:', result);
  }, []);

  // 处理 tab 切换
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'blood-chemistry') {
      navigate('/blood-chemistry');
    } else if (key === 'task-history') {
      navigate('/task-history');
    }
  };

  // Tab 列表配置
  const tabList = [
    {
      tab: 'A5标准化验单提取',
      key: 'blood-chemistry',
      icon: <ExperimentOutlined />,
    },
    {
      tab: '历史任务',
      key: 'task-history',
      icon: <HistoryOutlined />,
    },
  ];

  // 渲染 tab 内容
  const renderTabContent = () => {
    switch (activeTab) {
      case 'blood-chemistry':
        return (
          <div className="animate-float" style={{ animationDuration: '6s' }}>
            <BloodChemistryExtractor
              selectedFiles={selectedFiles}
              onFilesSelected={handleFilesSelected}
              onClearFiles={handleClearFiles}
              onProcessFiles={handleProcessFiles}
              isProcessing={isProcessing}
              disabled={isProcessing}
            />
            
            {taskId && (isProcessing || processCompleted) && (
              <div style={{ marginTop: 24 }}>
                <ProcessStatus
                  taskId={taskId}
                  onStatusChange={handleTaskStatusChange}
                  onDownload={handleDownload}
                />
              </div>
            )}
          </div>
        );
      case 'task-history':
        return (
          <div className="glass-card" style={{ padding: 24 }}>
            <TaskHistory />
          </div>
        );
      default:
        return null;
    }
  };

  // 渲染登录提示
  const renderLoginAlert = () => {
    if (isAuthenticated) return null;
    
    return (
      <Alert
        message={
          <span style={{ fontWeight: 600 }}>需要登录使用</span>
        }
        description={
          <div style={{ marginTop: 4 }}>
            请先登录以使用完整功能。
            <Button 
              type="link" 
              size="small" 
              onClick={handleLogin}
              style={{ padding: '0 4px', height: 'auto', fontWeight: 600 }}
            >
              立即登录
            </Button>
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              • 会员特权：批量处理 (2000张) • 极速提取 • 历史记录保存
            </div>
          </div>
        }
        type="info"
        showIcon
        closable
        className="glass-panel"
        style={{
          margin: '16px 24px',
          borderRadius: '12px',
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          border: '1px solid rgba(79, 70, 229, 0.1)',
          boxShadow: '0 4px 12px rgba(79, 70, 229, 0.05)'
        }}
      />
    );
  };

  return (
    <ProLayout
      title="MedSyntix"
      logo={logoImage}
      location={location}
      menuDataRender={menuDataRender}
      avatarProps={renderAvatarProps()}
      onMenuHeaderClick={() => navigate('/blood-chemistry')}
      token={{
        header: {
          colorBgHeader: 'rgba(255, 255, 255, 0.6)',
          colorHeaderTitle: token.colorPrimary,
          colorTextMenu: '#64748b',
          colorTextMenuSelected: token.colorPrimary,
          colorBgMenuItemSelected: 'rgba(79, 70, 229, 0.08)',
        },
        sider: {
          colorMenuBackground: 'transparent',
          colorBgMenuItemCollapsedElevated: 'transparent',
          colorMenuItemDivider: 'transparent',
        },
        pageContainer: {
          paddingBlockPageContainerContent: 24,
          paddingInlinePageContainerContent: 24,
          colorBgPageContainer: 'transparent',
        }
      }}
      headerTitleRender={(logo, title) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '0 12px'
          }}
          onClick={() => navigate('/blood-chemistry')}
        >
          <img 
            src={logoImage} 
            alt="MedSyntix" 
            style={{ 
              height: '36px', 
              width: '36px', 
              marginRight: '12px',
              borderRadius: '10px',
              boxShadow: '0 4px 10px rgba(79, 70, 229, 0.2)'
            }} 
          />
          <span className="text-gradient" style={{ 
            fontSize: '22px', 
            fontWeight: '700', 
            letterSpacing: '-0.5px'
          }}>
            MedSyntix
          </span>
        </div>
      )}
      footerRender={() => (
        <div style={{ 
          textAlign: 'center', 
          padding: '32px 0 24px 0',
          fontSize: '13px',
          color: '#94a3b8'
        }}>
          <div style={{ marginBottom: 8 }}>
            MedSyntix Intelligent Medical Analysis Platform
          </div>
          <div>
            ©{new Date().getFullYear()} MedSyntix. All rights reserved.
          </div>
        </div>
      )}
      menuItemRender={(item, dom) => (
        <div
          onClick={(e) => {
            if (item.path && (!item.children || item.children.length === 0)) {
              e.preventDefault();
              navigate(item.path);
            }
          }}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 8,
            fontWeight: 500 
          }}
        >
          {dom}
        </div>
      )}
      layout="mix"
      contentWidth="Fluid"
      fixedHeader
      fixSiderbar
      style={{
        background: 'transparent',
        minHeight: '100vh'
      }}
    >
      <div style={{ minHeight: '80vh' }}>
        {renderLoginAlert()}
        <Routes>
          <Route path="/" element={<Navigate to="/blood-chemistry" replace />} />
          <Route path="/lab-extraction" element={<Navigate to="/blood-chemistry" replace />} />
          <Route 
            path="/blood-chemistry"
            element={
              <PageContainer
                title={<span style={{ fontSize: 24, fontWeight: 600 }}>A5标准化验单提取</span>}
                subTitle="Intelligent OCR & Analysis"
                tabList={tabList}
                tabActiveKey={activeTab}
                onTabChange={handleTabChange}
                header={{
                  style: {
                    background: 'transparent',
                    marginBottom: 24
                  }
                }}
                tabProps={{
                  type: 'card',
                  className: 'glass-tabs'
                }}
              >
                {renderTabContent()}
              </PageContainer>
            } 
          />
          <Route 
            path="/task-history" 
            element={
              <PageContainer
                title={<span style={{ fontSize: 24, fontWeight: 600 }}>历史任务</span>}
                subTitle="Task Management"
                tabList={tabList}
                tabActiveKey={activeTab}
                onTabChange={handleTabChange}
                header={{
                  style: {
                    background: 'transparent',
                    marginBottom: 24
                  }
                }}
                tabProps={{
                  type: 'card',
                  className: 'glass-tabs'
                }}
              >
                {renderTabContent()}
              </PageContainer>
            } 
          />
          <Route 
            path="/buy-membership" 
            element={
              <PageContainer
                title="升级会员"
                header={{ style: { background: 'transparent' } }}
              >
                <BuyMembership />
              </PageContainer>
            } 
          />
          <Route path="/auth/wechat/callback" element={<WeChatCallback />} />
        </Routes>
      </div>

      {/* 认证模态框 - Added glass styling */}
      <AuthModal
        visible={authModalVisible}
        onClose={() => setAuthModalVisible(false)}
      />

      {/* 会员信息模态框 */}
      <MemberInfo
        visible={memberInfoVisible}
        onClose={() => setMemberInfoVisible(false)}
        memberInfo={memberInfo}
        onRefresh={fetchMemberInfo}
      />
    </ProLayout>
  );
};

export default ProLayoutApp;
